import { JSX, useId, useRef } from 'react'

import * as Select from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { Controller, FieldValues, Path } from 'react-hook-form'
import { usePrevious } from 'react-use'

import {
  DISABLED_INPUT_CLASSNAME,
  DisabledReasonTooltip,
  Group,
  Tooltip,
} from '~/components/component-library'
import { areOptionValuesEqual } from '~/components/component-library/SelectInput/util'
import { ErrorFooter, visibleError } from '~/components/inputs/ErrorFooter'
import { cn } from '~/utils/tailwind-utils'

import type {
  SelectInputInnerProps,
  SelectInputProps,
} from '~/components/component-library/SelectInput/types'

export const SelectInput = <
  ValueType = unknown,
  FormType extends FieldValues = FieldValues,
>(
  props: SelectInputProps<ValueType, FormType>,
): JSX.Element => {
  const previousOptions = usePrevious(props.options)

  if (props.control || props.inputName) {
    return (
      <Controller
        control={props.control}
        name={props.inputName as Path<FormType>}
        rules={{
          required: {
            value: !props.optional,
            message: props.requiredErrorMessage ?? 'Required',
          },
        }}
        render={({ field, fieldState, formState }) => {
          const message = visibleError(fieldState.error, {
            isTouched: fieldState.isTouched,
            isEmpty:
              field.value === undefined ||
              field.value === null ||
              field.value === '',
            isSubmitted: formState.isSubmitted,
          })
          return (
            <SelectInputInner
              tabIndex={props.tabIndex}
              error={message}
              selectedValue={field.value ?? props.selectedOption}
              onSelect={(value) => {
                // When options are changed from empty to non-empty after a while,
                // default behavior of radix's select is to select the first option
                // because a select with options can't be empty.
                //
                // However, by doing so it throws away whatever was selected before.
                // This is a bit hacky but couldn't find a better way to handle this.
                // What this does is that it preserves the previous value when options
                // are changed from empty to non-empty if the previous value is in the
                // new options.
                const previousValue = field.value ?? props.selectedOption
                if (
                  previousOptions &&
                  previousOptions.length === 0 &&
                  previousValue !== null &&
                  previousValue !== undefined &&
                  props.options.length > 0 &&
                  props.options.some((option) =>
                    areOptionValuesEqual(option.value, previousValue),
                  )
                ) {
                  field.onChange(previousValue)
                  props.selectValue?.(previousValue)
                  return
                }

                field.onChange(value)
                props.selectValue?.(value)
              }}
              secondaryDisplay={props.secondaryDisplay}
              {...props}
            />
          )
        }}
      />
    )
  } else {
    return (
      <SelectInputInner
        tabIndex={props.tabIndex}
        selectedValue={props.selectedOption}
        onSelect={props.selectValue}
        secondaryDisplay={props.secondaryDisplay}
        {...props}
      />
    )
  }
}

const SelectInputInner = <ValueType,>({
  options,
  selectedValue,
  onSelect,
  placeholder,
  disabled,
  tabIndex,
  label,
  footer,
  description,
  tooltip,
  size = 'base',
  secondaryDisplay,
  error,
  disabledReason,
  selectedIndicator = false,
}: SelectInputInnerProps<ValueType>) => {
  const selectedIndex = options.findIndex((option) =>
    areOptionValuesEqual(option.value, selectedValue),
  )
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined
  // Use empty string instead of undefined to keep Radix Select in controlled mode
  const currentValue = selectedIndex >= 0 ? selectedIndex.toString() : ''
  const triggerContent = selectedOption ? selectedOption.label : placeholder
  const labelId = useId()
  const contentId = useId()

  // Shared so the selected-indicator checkmark always matches the trigger chevron.
  const iconSize = size === 'small' ? 'size-4' : 'size-5'

  // A keyboard close returns focus to the trigger (Radix default) so keyboard
  // users keep their place; a click-outside instead keeps focus where the user
  // clicked — and off the trigger, so no focus ring lingers.
  const dismissedByOutsidePointerRef = useRef(false)

  const select = (
    <div
      className={cn('w-full overflow-hidden', {
        'cursor-not-allowed': disabled,
      })}
    >
      {label && (
        <span id={labelId} hidden>
          {label}
        </span>
      )}
      <Select.Root
        disabled={disabled}
        value={currentValue}
        onValueChange={(optionKey) => {
          const optionIndex = Number(optionKey)

          if (!Number.isNaN(optionIndex) && options[optionIndex]) {
            onSelect(options[optionIndex].value)
          }
        }}
      >
        <Select.Trigger
          className={cn(
            'rounded-lg outline-none ring-1 ring-inset transition',
            error
              ? 'ring-error focus-visible:ring-2 focus-visible:ring-error data-[state=open]:ring-2 data-[state=open]:ring-error'
              : 'ring-default focus-visible:ring-2 focus-visible:ring-focus data-[state=open]:ring-2 data-[state=open]:ring-focus',
            disabled
              ? `${DISABLED_INPUT_CLASSNAME} bg-surface-raised`
              : 'bg-surface-raised hover:bg-surface-hover',
            'group flex w-full select-none items-center justify-between gap-2',
            {
              'py-2.5 pl-3.5 pr-3 text-base': size === 'base',
              'py-1.5 pl-3 pr-2 text-sm': size === 'small',
              // Disabled: pass hover through to the wrapper, which carries
              // the not-allowed cursor (and the disabledReason tooltip) —
              // the disabled button swallows those pointer events itself.
              'pointer-events-none': disabled,
            },
          )}
          tabIndex={tabIndex}
          // Group renders no <label> to point at, so the trigger is named by a
          // hidden copy of the label plus its own content, keeping the
          // selected value in the announced name.
          aria-labelledby={label ? `${labelId} ${contentId}` : undefined}
        >
          <div className="flex min-w-0 flex-1 items-center justify-start gap-2">
            {selectedOption?.icon && (
              <div className="flex-shrink-0">{selectedOption.icon}</div>
            )}
            {/* Only string content gets the trigger's ellipsis; rich content
                (e.g. a two-line label or placeholder) truncates its own lines,
                and `truncate`'s overflow-hidden would clip taller content
                vertically. */}
            <span
              id={contentId}
              className={cn('min-w-0 flex-1 text-left', {
                truncate: typeof triggerContent === 'string',
                'text-placeholder': !selectedOption,
              })}
            >
              {triggerContent}
            </span>
          </div>
          <Select.Icon asChild>
            <ChevronDown
              className={cn(
                'flex-shrink-0 transition-transform duration-150 ease-linear',
                iconSize,
                error
                  ? 'text-error'
                  : selectedOption
                    ? 'text-secondary'
                    : 'text-placeholder',
              )}
            />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content
            side="bottom"
            align="start"
            sideOffset={4}
            position="popper"
            collisionPadding={8}
            onPointerDownOutside={() => {
              dismissedByOutsidePointerRef.current = true
            }}
            onCloseAutoFocus={(event) => {
              if (dismissedByOutsidePointerRef.current) {
                event.preventDefault()
              }
              dismissedByOutsidePointerRef.current = false
            }}
            className={cn(
              'z-[99] p-1 outline-none focus:outline-none',
              'anchored flex flex-col gap-0.5',
              // Trigger extends straight down — cancel anchored's scale-in
              'data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100',
            )}
            style={{ width: 'var(--radix-select-trigger-width)' }}
          >
            <Select.Viewport className="group/options max-h-48 overflow-y-auto [&_[data-select-option-only]]:block">
              {options.map((option, index) => {
                const hasDisabledTooltip = !!(
                  option.disabled && option.disabledReason
                )
                const item = (
                  <Select.Item
                    key={index.toString()}
                    value={index.toString()}
                    textValue={option.textValue}
                    disabled={option.disabled}
                    className={cn(
                      'flex w-full cursor-pointer select-none items-center justify-start gap-2 rounded-md',
                      'outline-none transition-colors',
                      'data-[highlighted]:bg-surface-muted',
                      'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
                      {
                        // Only when a tooltip wraps this item: Radix refocuses
                        // the list on every pointermove over a disabled item,
                        // which keeps the wrapper's tooltip from opening —
                        // make the item inert so only the wrapper sees the
                        // pointer.
                        'data-[disabled]:pointer-events-none':
                          hasDisabledTooltip,
                      },
                      // Highlight the selected option on open, but only until the
                      // user starts navigating: once any option is highlighted
                      // (keyboard/pointer) the list `:has` a highlighted item, so
                      // this rule drops out and the highlight tracks the active
                      // row via `data-[highlighted]` instead of sticking here.
                      'group-[&:not(:has([data-highlighted]))]/options:data-[state=checked]:bg-surface-muted',
                      size === 'small'
                        ? 'px-2 py-1.5 text-sm'
                        : 'px-2.5 py-2 pr-2 text-base',
                    )}
                  >
                    {option.icon && (
                      <div className="flex-shrink-0">{option.icon}</div>
                    )}
                    <Select.ItemText
                      asChild={typeof option.label !== 'string'}
                      className="flex-1 text-primary"
                    >
                      {option.label}
                    </Select.ItemText>
                    {option.suffix && (
                      <div className="ml-auto flex-shrink-0">
                        {option.suffix}
                      </div>
                    )}
                    {selectedIndicator && (
                      <div
                        className={cn(
                          'flex flex-shrink-0 items-center justify-center',
                          iconSize,
                        )}
                      >
                        <Select.ItemIndicator>
                          {/* The check glyph fills far more of its box than the
                              chevron, so at an equal box it reads larger. Render
                              it one step down inside the chevron-width slot so the
                              two icons look optically matched while the reserved
                              slot keeps the column aligned. */}
                          <Check
                            strokeWidth={2.25}
                            className={cn(
                              'text-secondary',
                              size === 'small' ? 'size-3.5' : 'size-4',
                            )}
                          />
                        </Select.ItemIndicator>
                      </div>
                    )}
                  </Select.Item>
                )

                return hasDisabledTooltip ? (
                  <Tooltip
                    key={index.toString()}
                    // The item suppresses its own pointer events so hover
                    // reaches this wrapper, which means the not-allowed cursor
                    // has to live here too.
                    triggerClassName="pointer-events-auto w-full cursor-not-allowed"
                    // Select.Content is z-[99] (above the Tooltip's default
                    // z-50) so the tooltip needs to render above the open
                    // dropdown rather than behind it.
                    contentClassName="z-[100]"
                    content={option.disabledReason}
                  >
                    {item}
                  </Tooltip>
                ) : (
                  item
                )
              })}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  )

  return (
    <Group
      label={label}
      footer={error ? <ErrorFooter message={error} /> : footer}
      description={description}
      tooltip={tooltip}
      secondaryDisplay={secondaryDisplay}
    >
      <DisabledReasonTooltip
        disabled={disabled}
        disabledReason={disabledReason}
        className="relative flex w-full"
      >
        {select}
      </DisabledReasonTooltip>
    </Group>
  )
}

export type {
  SelectInputProps,
  SelectInputFormProps,
  SelectInputControlledProps,
  SelectInputInnerProps,
} from '~/components/component-library/SelectInput/types'
